<script setup lang="ts">
//#region import
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { Point } from 'highcharts';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useLodash } from '@/setup/lodash';
import colors from '@/views/Dashboard/helper/colors';
import formaterY, { type ColumnFormatType } from '@/views/Dashboard/helper/customFormat';
import type { IconControlItem, DateRange } from '@/types/core';
import type { ChartProps } from 'highcharts-vue';
import type { PostDashboardsStatsData, PostDashboardsStatsResponse } from '@/types/openapi-ts';
import type { 
  DashboardPanel, 
  DashboardPanelChart, 
  DashboardPanelTypes, 
  DashboardsStatsLineChart, 
  DashboardsStatsPieChart,
  FilterRecordExtended, 
} from '@/types/dashboard';
import type { ExtractByFields } from '@/types/util';
import type { AvaliableLocales } from '@/i18n';
//#endregion

//#region types
export interface Props {
    chart: DashboardPanel,
    rowIndex?: number,
    index?: number,
    preview?: boolean,
    isEdit?: boolean,
    isLoading?: boolean,
    savePeriod?: boolean,
    animation?: boolean,
    dashboardId?: string,
    period?: DateRange,
    propCurrencyCode?: string
}

export interface Emits {
  'removeMetric': [rowIndex: number, type: DashboardPanelTypes, index: number],
  'editMetric': [rowIndex: number, type: DashboardPanelTypes, index: number, data: DashboardPanel], 
  'emitDataReport': [rowIndex: number, type: DashboardPanelTypes, index: number, report: PostDashboardsStatsResponse['payload']], 
  'changeForecast': [rowIndex: number, type: DashboardPanelTypes, index: number, enableForecast: boolean],
};
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  preview: false,
  isEdit: false,
  isLoading: false,
  savePeriod: false,
  animation: false,
  dashboardId: '',
  period: () => [],
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const store = useTypedStore();
const i18n = useI18n();
const { t } = i18n;
const api = useApi();
const {
  currencyCode,
  currencyLabel,
  formatMetric,
} = useFormatCurrency();
const _ = useLodash();
//#endregion

//#region state
const loading = ref(false);
const report = ref({} as PostDashboardsStatsResponse['payload']);

const series = ref<ReturnType<typeof formattedSeries>>([]);
const xAxis = ref<string[]>([]);
const formatMetricSeries = ref<string[] | string>('');
const titleYSeries = ref('');
const chartVisualization = ref<'line' | 'bar' | 'pie'>('line');

const forecastValue = ref(false);
const disabledForecast = ref(false);

const seriesYAxisNumber = ref<number[]>([]);
const configChart = ref(configChartMethod());
//#endregion

//#region computed
const reportsSettings = computed(() => store.getters['reports/reportsSettings']);
const stateDashboardFilter = computed(() => store.state.dashboards.stateDashboardFilter);

const computedheaderTooltip = computed(() => {
  return Array.isArray(computedMetricName.value)
    ? ''
    : (getTranslateMetrics(computedMetricName.value!)?.description || '');
});

const filterIsEmpty = computed(() => {
  if (props.chart.filters === null) return true;
  const { search, filters } = props.chart.filters;
  const countKey = Object.keys(props.chart.filters).length;
  const func = (count: number, filter: FilterRecordExtended) => {
    const keyArray = Object.keys(filter);
    if (count === 1) {
      return keyArray.includes('filters') || keyArray.includes('search');
    }
    if (count === 2) {
      return keyArray.includes('filters') && keyArray.includes('search');
    }
    return false;
  };
  return (
    _.isEmpty(search)
    && _.isEmpty(filters)
    && func(countKey, props.chart.filters)
  );
});

const controlsData = computed<IconControlItem[]>(() => {
  return [
    {
      title: t('dashboards.other.edit'),
      icon: 'pen',
      color: 'var(--text-primary)',
      clickHandler: () => editMetric(props.rowIndex!, 'chart', props.index!, props.chart),
    },
    {
      title: t('dashboards.other.delete'),
      color: 'var(--text-negative)',
      icon: 'trash-alt',
      clickHandler: () => removeMetric(props.rowIndex!, props.index!),
    },
  ];
});

const computedMetricName = computed(() => {
  const metricSeries = 'series_group_by_metric_name' in props.chart && props.chart.series_group_by_metric_name !== null
    ? props.chart.series_group_by_metric_name
    : props.chart.series!.map((s) => s.metric_name);
  return metricSeries;
});

const paramsData = computed(() => {
  const period = {
    from: format(props.period[0] as string, 'yyyy-MM-dd HH:mm:ss'),
    to: format(props.period[1] as string, 'yyyy-MM-dd HH:mm:ss'),
  };
  const panel: DashboardPanel = {
    ...props.chart,
    enable_forecast: forecastValue.value,
    series: props.chart.series ? props.chart.series : [],
  };

  return {
    panel,
    filters: stateDashboardFilter.value,
    ...period,
    currency_code: currencyCode.value,
  } as PostDashboardsStatsData['body'];
});

const forecastConfig = computed(() => {
  if (!forecastValue.value) {
    return {
      text: t('dashboards.other.show_forecast'),
      icon: 'eye',
      color: 'var(--text-link)',
    };
  }
  return {
    text: t('dashboards.other.hide_forecast'),
    icon: 'eye-slash',
    color: 'var(--text-negative)',
  };
});
//#endregion

//#region methods
async function updateDashboardPanelForecast(enable_forecast: boolean) {
  await api.updateDashboardPanelForecast(
    props.dashboardId,
    props.chart.id,
    { enable_forecast },
  );

  emit(
    'changeForecast',
    props.rowIndex!,
    'chart',
    props.index!,
    enable_forecast,
  );
}

async function handleForecastSwitch() {
  disabledForecast.value = true;
  forecastValue.value = !forecastValue.value;
  await updateDashboardPanelForecast(forecastValue.value);
  await getReport(undefined, 'localeForecost');
  disabledForecast.value = false;
}

async function getDashboardsStats(params: PostDashboardsStatsData['body']) {
  const {
    data: { payload },
  } = await api.dashboardsStats(params);
  report.value = payload;
  emit(
    'emitDataReport',
    props.rowIndex!,
    'chart',
    props.index!,
    report.value,
  );
}

function editMetric(rowIndex: number, type: DashboardPanelTypes, index: number, data: DashboardPanel) {
  emit('editMetric', rowIndex, type, index, data);
}

function removeMetric(rowIndex: number, index: number) {
  emit('removeMetric', rowIndex, 'chart', index);
}

function getTranslateMetrics(metric: string) {
  return formatMetric(reportsSettings.value.metrics.find((e) => e.column_name === metric));
}

async function getReport(type?: string, flag?: 'localeForecost') {
  if (flag !== 'localeForecost') {
    forecastValue.value = !!(props.chart as DashboardPanelChart).enable_forecast;
  }

  if (type === 'getLocalData') {
    report.value = props.chart.report!;
  } else {
    await getDashboardsStats(paramsData.value);
    loading.value = false;
  }

  chartVisualization.value = (props.chart as DashboardPanelChart).chart_visualization!;
  
  series.value = formattedSeries(
    chartVisualization.value !== 'pie'
      ? (report.value as DashboardsStatsLineChart).series
      : (report.value as DashboardsStatsPieChart).data,
  );
  xAxis.value = chartVisualization.value !== 'pie'
    ? (report.value as DashboardsStatsLineChart).x_axis?.map((x) => x.formatted.toString())
    : [];

  const metricSeries = 'series_group_by_metric_name' in props.chart && props.chart.series_group_by_metric_name !== null
    ? props.chart.series_group_by_metric_name
    : props.chart.series!.map((s) => s.metric_name);

  formatMetricSeries.value = Array.isArray(metricSeries)
    ? metricSeries.map((s) => getTranslateMetrics(s).column_format)
    : getTranslateMetrics(metricSeries!).column_format;

  titleYSeries.value = Array.isArray(metricSeries)
    ? ''
    : getTranslateMetrics(metricSeries!).title;

  configChart.value = configChartMethod(
    formatMetricSeries.value as string[],
    i18n.locale.value as AvaliableLocales,
  );
};

function formattedSeries(seriesData: DashboardsStatsLineChart['series'] | DashboardsStatsPieChart['data']) {
  let series = [];
  if (chartVisualization.value === 'pie' && (report.value as DashboardsStatsPieChart).others) {
    series = [
      ...(seriesData as DashboardsStatsPieChart['data']),
      {
        ...(report.value as DashboardsStatsPieChart).others,
        dimension: t('dashboards.other.others'),
      },
    ];
  } else {
    series = (seriesData as DashboardsStatsLineChart['series']);
  }

  return series?.map((ser, i) => {
    type DataType = typeof data;
    type DataTypeLineChart = ExtractByFields<DataType[number], 'opacity'>[];

    const fnGetForecastRange = (data: DataType) => {
      const fi = (data as DataTypeLineChart).findIndex((e) => e.opacity === 0.99);
      if (fi === -1) return data.length;
      return fi;
    };

    const zoneConfig = (data: DataType) => {
      const typeLine = chartVisualization.value === 'bar' ? 'dash' : 'dot';
      const valConf = {
        zoneAxis: 'x',
        zones: [
          {
            value: data.length - (chartVisualization.value === 'bar'
              ? (data.length - fnGetForecastRange(data))
              : (data.length - fnGetForecastRange(data) + 1)
            ),
          },
          {
            dashStyle: forecastValue.value ? typeLine : 'line',
            className: forecastValue.value ? 'forecast-section' : '',
          },
        ],
      };
      return valConf;
    };

    const name = chartVisualization.value !== 'pie' 
      ? (ser as DashboardsStatsLineChart['series'][number]).name 
      : (ser as DashboardsStatsPieChart['data'][number]).dimension;
    
    const data = chartVisualization.value !== 'pie'
      ? (ser as DashboardsStatsLineChart['series'][number]).values.map((val) => ({
        y:
              val.raw_forecasted !== undefined && forecastValue.value
                ? Number(val.raw_forecasted)
                : Number(val.raw),
        yFormatted: val.formatted,
        marker: {
          radius: 4.5,
          lineColor: colors[i],
          lineWidth: 1,
          fillColor: '#ffffff',
        },
        opacity: (val.raw_forecasted !== undefined && forecastValue.value) ? 0.99 : 1,
      }))
      : [
        {
          y: Number((ser as DashboardsStatsPieChart['data'][number]).raw),
          yFormatted: (ser as DashboardsStatsPieChart['data'][number]).formatted,
          marker: {
            radius: 4.5,
            lineColor: colors[i],
            lineWidth: 1,
            fillColor: '#ffffff',
          },
        },
      ];

    return {
      name,
      color: colors[i],
      borderColor: colors[i],
      data,
      ...zoneConfig(data),
    };
  });
}

function configChartMethod(columnFormat?: string[], locale?: AvaliableLocales) {
  const currency = currencyLabel;
  seriesYAxisNumber.value = [];
  if (Array.isArray(columnFormat)) {
    const uniqAxis = _.uniq(columnFormat);
    seriesYAxisNumber.value = columnFormat.map((el) => uniqAxis.indexOf(el));
  }

  const typeChart = () => {
    if (chartVisualization.value === 'line') {
      return 'spline';
    }
    if (chartVisualization.value === 'bar') {
      return 'column';
    }
    return 'pie';
  };

  const seriesData = (): NonNullable<ChartProps['options']['series']> => {
    if (typeChart() === 'pie') {
      return series.value
        ? [
          {
            type: chartVisualization.value,
            name: titleYSeries.value,
            data: series.value.reduce((acc, el, i) => {
              acc[i] = {
                name: el.name as string,
                color: el.color,
                y: el.data.reduce((sum, item) => sum + item.y, 0),
                //@ts-expect-error в dts yFormatted отсутсвует, но в рантайме он есть
                yFormatted: _.get(el, ['data', 0, 'yFormatted']),
              };
              return acc;
            }, [] as Array<Point>),
          },
        ] as NonNullable<ChartProps['options']['series']>
        : [];
    }

    return series.value?.map((el, i) => ({
      ...el,
      yAxis: seriesYAxisNumber.value[i],
    } as NonNullable<ChartProps['options']['series']>[number]));
  };

  const legend: NonNullable<ChartProps['options']['legend']> = {
    enabled: true,
    align: 'center',
    verticalAlign: 'bottom',
    layout: 'horizontal',
    itemWidth: undefined,
    maxHeight: 40,
    //@ts-expect-error dts ругается
    className: 'highcharts-no-tooltip',
    navigation: {
      activeColor: '#666666',
    },
  };

  const yAxis: NonNullable<ChartProps['options']['yAxis']> = Array.isArray(columnFormat)
    ? _.uniq(columnFormat).map((cf, i) => ({
      title: {
        text: typeChart() === 'pie' ? '' : titleYSeries.value,
      },
      labels: {
        formatter() {
          return cf ? formaterY(this.value, cf as ColumnFormatType, locale, currency.value) : this.value.toString();
        },
      },
      opposite: i > 0,
      gridLineDashStyle: 'ShortDash',
      gridLineColor: '#dedede',
      gridLineWidth: 1,
    }))
    : {
      title: {
        text: typeChart() === 'pie' ? '' : titleYSeries.value,
      },
      labels: {
        formatter() {
          return columnFormat
            ? formaterY(this.value, columnFormat, locale, currency.value)
            : this.value.toString();
        },
      },
      gridLineDashStyle: 'ShortDash',
      gridLineColor: '#dedede',
      gridLineWidth: 1,
    };

  const tooltip: NonNullable<ChartProps['options']['tooltip']> = Array.isArray(columnFormat)
    ? {
      useHTML: true,
      outside: true,
      style: {
        zIndex: 3000,
      },
      formatter() {
        return this.points
          ? this.points.reduce(
            (s, point, i) => `${s}<br/><span  style="color:${
                point.color
              }; font-size: 16px">•</span>${
                point.series.name
              }: <span style="font-weight: 900">${
                //@ts-expect-error в dts yFormatted отсутсвует, но в рантайме он есть
                point.point.yFormatted || (columnFormat[i] ? formaterY(point.y, columnFormat[i], locale) : point.y)
              }</span>`,
            `<b>${this.x}</b>`,
          )
          : `${this.key}<br/>${this.point.series.name}: ${
              //@ts-expect-error в dts yFormatted отсутсвует, но в рантайме он есть
              this.point.yFormatted || (columnFormat ? formaterY(this.point.y, columnFormat, locale) : this.point.y)
            }`;
      },
      shared: true,
    }
    : {
      outside: true,
      formatter() {
        return this.points
          ? this.points.reduce(
            (s, point) => `${s}<br/>${point.series.name}: ${
              //@ts-expect-error в dts yFormatted отсутсвует, но в рантайме он есть
              point.point.yFormatted || (columnFormat ? formaterY(point.y, columnFormat, locale) : point.y)
            }`,
            `<b>${this.x}</b>`,
          )
          : `${this.key}<br/>${this.point.series.name}: ${
            //@ts-expect-error в dts yFormatted отсутсвует, но в рантайме он есть
            this.point.yFormatted || (columnFormat ? formaterY(this.point.y, columnFormat, locale) : this.point.y)
          }`;
      },
      shared: true,
    };

  const result: ChartProps['options'] = {
    chart: {
      type: typeChart(),
      renderTo: 'container',
    },
    xAxis: {
      visible: typeChart() !== 'pie',
      categories: xAxis.value,
    },
    yAxis,
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    legend,
    tooltip,
    series: seriesData(),
    plotOptions: {
      spline: {
        marker: {
          symbol: 'curcle',
          radius: 3.5,
          lineWidth: 1,

          states: {
            hover: {
              //@ts-expect-error dts ругается
              enabledThreshold: 2,
              radius: 4.5,
              radiusPlus: 0,
              lineWidthPlus: 0.5,
            },
          },
        },
      },
      line: {
        marker: {
          symbol: 'curcle',
          radius: 4.5,
          lineWidth: 1,
          states: {
            hover: {
              //@ts-expect-error dts ругается
              enabledThreshold: 2,
              radius: 4.5,
              radiusPlus: 0,
              lineWidthPlus: 0.5,
            },
          },
        },
      },
      pie: {
        animation: {
          defer: 0,
          duration: props.animation ? 1000 : 0,
        },
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          distance: 20,
        },
      },
      column: {
        borderRadius: 4,
      },
    },
  };

  return result;
}
//#endregion

//#region watch
watch(() => props.propCurrencyCode, (newVal) => {
  currencyCode.value = newVal;
}, {immediate: true});

watch(currencyCode, () => {
  getReport();
});
//#endregion

defineExpose({
  loading,
  getReport,
});
</script>

<template>
  <div
    :class="[
      'dashboard-chart',
      {'dashboard-chart_loading': loading || isLoading}
    ]"
  >
    <div class="dashboard-chart__title">
      <UiTitle
        :filtered="!filterIsEmpty"
        :tooltip="computedheaderTooltip"
        class="t:font-medium"
      >
        {{ chart.name }}
      </UiTitle>
      <UiAction
        v-if="!isEdit && chartVisualization !== 'pie'"
        :action-name="forecastConfig.text"
        :icon="forecastConfig.icon"
        :color="forecastConfig.color"
        class="dashboard-chart__forecast"
        @click="handleForecastSwitch"
      />
      <div
        v-if="isEdit"
        class="t:flex t:space-x-12 t:ml-8"
      >
        <ActionIcon
          v-for="control in controlsData"
          :key="control.title"
          :icon="control.icon"
          :color="control.color"
          :tooltip="control.title"
          :is-overlay="false"
          @click="control.clickHandler"
        />
      </div>
    </div>
    <!-- eslint-disable-next-line vue/component-name-in-template-casing -->
    <highcharts
      class="dashboard-chart__chart"
      :options="configChart"
    />
  </div>
</template>

<style lang="scss">
.dashboard-chart{
  width: 100%;
  border-radius: 8px;
  background: var(--background-surface);
  position: relative;
  overflow: hidden;
  &_loading {
    &::after {
      content: '';
      position: absolute;
      padding: 16px;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background: var(--background-surface);
      background-size: 100% 100%;
      background-position-y: 0;
      background-repeat: no-repeat;
      background-image: url('@/assets/images/dashboard/graph-skeleton.svg');
      background-origin: content-box;
    }
  }

  &__title {
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__chart {
    width: 100%;
    height: 280px;
    .highcharts-background {
      fill: var(--background-surface);
    }
    .highcharts-credits {
      display: none;
    }
    .highcharts-legend-item {
      tspan {
        font-weight: 500;
      }
    }
    .highcharts-column-series .highcharts-point {
      stroke-width: 0;
    }

    .highcharts-column-series .forecast-section {
      fill-opacity: 0.2;
      stroke-width: 1;
    }
    .highcharts-legend-item rect.highcharts-point {
      y: 5;
    }
  }
}
</style>
